import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {Typography} from '@mui/material';
import {SimpleTreeView, TreeItem, TreeViewBaseItem} from '@mui/x-tree-view';
import {AxiosResponse} from 'axios';
import {t} from 'i18next';
import _, {isArray} from 'lodash';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  addNewGroup,
  updateSavedSearch,
  updateSaveSearchDetails,
} from '../../../api/EcaServices';
import {
  ONLY_DATE_SHOW_FORMAT,
  TOAST_TIMEOUT,
} from '../../../constants/constants';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {
  setAddGroupCheck,
  setManageSavedSearch,
  setSearchTreeItemList,
} from '../../../redux/slices/ecaAdhocSearchSlice';
import {RootState} from '../../../redux/store';
import {
  extractSavedSearchId,
  extractSelectedNodeId,
  formatNumberWithComma,
  getUTCDateTimeForDisplay,
  isEmpty,
} from '../../../utils/utils';
import AurButton from '../../common/aur-button/AurButton';
import {AurDropdownButton} from '../../common/aur-button/AurDropdownButton';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import AurMenu from '../../common/aur-menu/AurMenu';
import AurTooltip from '../../common/aur-tooltip/AurTooltip';
import {SavedSearchDetails} from '../../eca/SavedSearchPanel';
import {ErrorDialog} from '../../pages/field-management/custom-field/ErrorDialog';
import {IUpdateSavedSearchReqBody} from '../eca-doc-list.util';
import {ExtendedTreeItemProps} from './EcaSaveSearch';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';

const DEFAULT_EXPANDED_IDS: string[] = [''];

const getIconFromFileType = (fileType: string) => {
  switch (fileType) {
    case 'folder':
      return <FolderIcon className="location-folder-icon" />;
    case 'public':
      return <SearchOutlinedIcon className="location-folder-icon" />;
    case 'private':
      return <PersonSearchOutlinedIcon className="location-folder-icon" />;
  }
};

export type TrackItemsType = {
  label: string;
  id: string;
};

interface AurLocationTreeViewProps {
  itemsList: TreeViewBaseItem<ExtendedTreeItemProps>[];
  setSelectedItem?: (value: string | null) => void;
  handleSelectedItem?: (
    value: TreeViewBaseItem<ExtendedTreeItemProps>,
    event?: React.SyntheticEvent
  ) => void;
  addFolderAllowed?: boolean;
  showTooltip?: boolean;
  getChildren?: (
    id: string,
    directory_structure_ids?: string
  ) => Promise<TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined>;
  fetchUpdatedRoot?: () => Promise<
    TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined
  >;
  selectedItem?: string;
  treeSelectedItems?: string[];
  multipleSelection?: boolean;
  multipleSelectedLabel?: string;
  isShowCountLabel?: boolean;
  handleNoResultMessage?: string;
  allowGlobalSelect?: boolean;
  defaultExpandedIds?: string[];
  setExpandedFolders?: (data: string[]) => void;
  showActions?: boolean;
  manageSavedSearch?: boolean;
  manageSearchRename?: boolean;
  savedSearchedDetails?: any;
  onTrackItemsUpdate?: (item: TrackItemsType[] | []) => void;
  handleSelect?: (
    item: string,
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => void;
  showCheckmark?: boolean;
  selectedNodeToMove?: string;
  preSelectedId?: string;
  updateTreeData?: React.Dispatch<
    React.SetStateAction<TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined>
  >;
  showTooltipAsNodeName?: boolean;
  isFromEcaSaveSearch?: boolean;
  isCreatingRootFolder?: boolean;
  isTemplate?: boolean;
  templateOperations?: TemplateOperations;
  allowRootRename?: boolean;
  hideDropdownMenu?: boolean;
}

interface TreeItem {
  id: string;
  label: string;
  fileType: string;
  children?: TreeItem[];
}

type TemplateOperations = {
  templeteClientId: string;
  setIsCreatingRootFolder: (state: boolean) => void;
  addNewGroup: (
    clientId: string,
    requestBody: {name: string; parentGroupId: string | null}
  ) => any;
  updateGroupTemplate: (
    groupId: string,
    requestBody: {
      name: string;
      parentGroupId: string | null;
    }
  ) => any;
  updateSavedSearchTemplate: (
    savedSearchId: string,
    requestBody: {name: string; groupId: string}
  ) => Promise<AxiosResponse<any, any>>;
};

const AurLocationTreeView = (props: AurLocationTreeViewProps) => {
  const {
    itemsList,
    setSelectedItem,
    handleSelectedItem,
    addFolderAllowed = false,
    showTooltip = false,
    getChildren,
    selectedItem,
    treeSelectedItems,
    multipleSelection,
    multipleSelectedLabel,
    isShowCountLabel = true,
    handleNoResultMessage,
    allowGlobalSelect = false,
    defaultExpandedIds = DEFAULT_EXPANDED_IDS,
    setExpandedFolders,
    showActions = false,
    manageSavedSearch = false,
    manageSearchRename = false,
    savedSearchedDetails,
    onTrackItemsUpdate,
    showCheckmark = false,
    handleSelect,
    selectedNodeToMove,
    preSelectedId = '',
    updateTreeData,
    showTooltipAsNodeName = false,
    isFromEcaSaveSearch = false,
    isCreatingRootFolder = false,
    isTemplate = false,
    templateOperations = null,
    fetchUpdatedRoot,
    allowRootRename = false,
    hideDropdownMenu = false,
  } = props;

  const [selectedItems, setSelectedItems] = useState<
    string | string[] | null | undefined
  >(selectedItem);
  const [treeData, setTreeData] = useState(itemsList);
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState<string | null | undefined>(
    ''
  );
  const [typedSpecialCharacter, setTypedSpecialCharacter] =
    useState<boolean>(false);
  const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);
  const [isRenameClicked, setIsRenameClicked] = useState<boolean>(false);
  const [newTempName, setNewTempName] = useState<string | null | undefined>('');
  const [selectedIds, setSelectedIds] = useState(preSelectedId);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [expandedIds, setExpandedIds] = useState<string[]>(defaultExpandedIds);
  const [count, setCount] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const {showToast} = useToast();

  const {globalSelectedItem, globalSelectedScopeItem} = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );
  const {clientId, projectId} = useSelector(
    (state: RootState) => state.reports
  );
  const {builder} = useSelector((state: RootState) => state.query);
  const {queryData} = useSelector((state: RootState) => state.ecaDocList);

  const {hasSavedSearchesPerm} = usePermissionsByPage(
    PAGE_NAME.INDEX_SEARCH_TAB
  );

  const dispatch = useDispatch();
  const handleRenameClick = () => {
    setIsRenameClicked(true);
    dispatch(setManageSavedSearch(false));
    if (selectedItems) {
      const item = isArray(selectedItems) ? null : selectedItems;
      setEditingNodeId(item);
      setCount(0);
      setNewFolderName(findNodeById(treeData, item as never)?.label);
      setNewTempName(findNodeById(treeData, item as never)?.label);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const handleUpdateSavedSearch = async (parentId: string) => {
    const reqBody: IUpdateSavedSearchReqBody = {
      name: newFolderName as string,
      parentGroupId: extractSavedSearchId(parentId),
    };

    try {
      await updateSavedSearch(
        params,
        extractSavedSearchId(selectedIds),
        reqBody
      );

      showToast(
        `${newTempName} ${t('ecaDocList.saveSearch.groupRenamed')} ${newFolderName}`,
        'success',
        TOAST_TIMEOUT,
        'center',
        'bottom'
      );
    } catch {
      showToast(
        t('ecaDocList.saveSearch.renameGroupError'),
        'error',
        TOAST_TIMEOUT,
        'center'
      );
    } finally {
      setIsRenameClicked(false);
    }
  };

  /**Handles saved group template update ops */
  const handleUpdateSavedSearchGroupTemplate = async (
    parentGroupId: string | null
  ) => {
    const reqBody: {name: string; parentGroupId: string | null} = {
      name: newFolderName as string,
      parentGroupId: !_.isNull(parentGroupId)
        ? extractSavedSearchId(parentGroupId)
        : null,
    };
    const groupId = extractSavedSearchId(selectedIds);

    try {
      await templateOperations?.updateGroupTemplate(groupId, reqBody);
      showToast(
        `${newTempName} ${t('ecaDocList.saveSearch.groupRenamed')} ${newFolderName}`,
        'success',
        TOAST_TIMEOUT,
        'center',
        'bottom'
      );
    } catch {
      showToast(
        t('ecaDocList.saveSearch.renameGroupError'),
        'error',
        TOAST_TIMEOUT,
        'center'
      );
    } finally {
      setIsRenameClicked(false);
    }
  };

  const handleUpdateName = async () => {
    const paramsClientProject = `?clientId=${clientId}&projectId=${projectId}`;
    const reqBody: SavedSearchDetails = {
      ...savedSearchedDetails,
      name: newFolderName as string,
    };
    if (queryData.queryBuilderMode) {
      reqBody.jsonLogicQuery = JSON.stringify(builder);
    }
    try {
      await updateSaveSearchDetails(
        paramsClientProject,
        extractSavedSearchId(savedSearchedDetails.id),
        reqBody
      );
      showToast(
        `${newTempName} ${t('ecaDocList.saveSearch.renamed')} ${newFolderName}`,
        'success',
        TOAST_TIMEOUT,
        'center',
        'bottom'
      );
    } catch {
      showToast(
        t('ecaDocList.saveSearch.renameError'),
        'error',
        TOAST_TIMEOUT,
        'center',
        'bottom'
      );
    }
  };

  const handleUpdateSavedSearchTemplate = async () => {
    const reqBody: {name: string; groupId: string} = {
      name: newFolderName as string,
      groupId: savedSearchedDetails.groupId,
    };

    const savedSearchTemplateId = savedSearchedDetails.id;
    try {
      await templateOperations?.updateSavedSearchTemplate(
        savedSearchTemplateId,
        reqBody
      );
      showToast(
        `${newTempName} ${t('ecaDocList.saveSearch.renamed')} ${newFolderName}`,
        'success',
        TOAST_TIMEOUT,
        'center',
        'bottom'
      );
    } catch {
      showToast(
        t('ecaDocList.saveSearch.renameError'),
        'error',
        TOAST_TIMEOUT,
        'center',
        'bottom'
      );
    }
  };

  const paramsClientProject = `?clientId=${clientId}&projectId=${projectId}`;

  useEffect(() => {
    if (updateTreeData) {
      updateTreeData(treeData);
    }
  }, [treeData]);

  useEffect(() => {
    setTreeData(itemsList);

    if (itemsList.length === 1 && defaultExpandedIds.length === 0) {
      const id = itemsList.map((item) => item.id);
      setExpandedIds(id);
      if (getChildren) {
        const directory_structure_ids = itemsList[0].directory_structure_ids;
        handleAddChildern(id[0], directory_structure_ids);
      }
      return;
    }

    setExpandedIds((prev) => {
      if (
        prev.length === defaultExpandedIds.length &&
        prev.every((id, index) => id === defaultExpandedIds[index])
      ) {
        return prev;
      }
      return defaultExpandedIds;
    });
  }, [itemsList, defaultExpandedIds]);

  useEffect(() => {
    setSelectedItems(multipleSelection ? treeSelectedItems : selectedItem);
  }, [multipleSelection, selectedItem, treeSelectedItems]);

  useEffect(() => {
    setExpandedFolders?.(expandedIds);
  }, [expandedIds, setExpandedFolders]);

  const handleClick = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const findPathById = (
    treeData: TreeItem[],
    id: string,
    path: TreeItem[] = []
  ): TreeItem[] | null => {
    for (const item of treeData) {
      const newPath: TreeItem[] = [
        ...path,
        {id: item.id, label: item.label, fileType: item.fileType},
      ];

      if (item.id === id) {
        // Remove base folder (first item) and last item if it's not a folder
        const filteredPath = newPath.slice(1);
        if (
          filteredPath.length > 1 &&
          filteredPath[filteredPath.length - 1].fileType !== 'folder'
        ) {
          filteredPath.pop();
        }
        return filteredPath;
      }

      if (item.children && item.children.length > 0) {
        const foundPath = findPathById(item.children, id, newPath);
        if (foundPath) {
          return foundPath;
        }
      }
    }
    return null;
  };

  const handleSelectedItemsChange = (
    event: React.SyntheticEvent,
    ids: string[] | string | null
  ) => {
    const selectedId = isArray(ids) ? ids?.[0] : ids;
    if (showCheckmark) {
      const parentFolderId = findParentById(treeData, selectedNodeToMove ?? '');

      const selectedNode = findNodeById(treeData, selectedId ?? '');
      if (!selectedNode) return;

      // getting the children even though move is not allowed
      if (selectedNode.fileType === 'folder' && getChildren) {
        handleAddChildern(
          selectedNode.id,
          selectedNode.directory_structure_ids
        );
      }
      //  Prevent selecting itself or parent folder or Disable files (only allow folders)
      if (
        selectedId === selectedNodeToMove ||
        selectedId === parentFolderId ||
        selectedNode.fileType !== 'folder'
      ) {
        return;
      }
    }
    if (
      selectedId &&
      !selectedId?.includes('loading') &&
      selectedId !== selectedNodeToMove
    ) {
      setSelectedItems(selectedId);
      const labelPath = findPathById(treeData, selectedId);
      if (onTrackItemsUpdate && labelPath) onTrackItemsUpdate(labelPath);
      setSelectedItem?.(selectedId);

      if (handleSelectedItem) {
        const selectedItemNode = findNodeById(treeData, selectedId);
        setSelectedIds(selectedItemNode?.id as string);
        selectedItemNode && handleSelectedItem(selectedItemNode, event);
      }
      dispatch(setSearchTreeItemList(treeData));
    }
  };

  const closeHandler = () => {
    setTypedSpecialCharacter(false);
    setOpenErrorDialog(false);
    setTimeout(() => {
      inputRef.current?.focus();

      // Optional: move cursor to end of input
      if (inputRef.current) {
        const length = inputRef.current.value.length;
        inputRef.current.setSelectionRange(length, length);
      }
    }, 0);
  };

  const selectedItemHandler = (
    e: any,
    node: TreeViewBaseItem<ExtendedTreeItemProps>,
    preSelectedItemLocation: boolean
  ) => {
    if (preSelectedItemLocation) {
      e.stopPropagation(); // Prevent selection for the current location
      return;
    }

    const hasLoadingChild = node.children?.filter((i) => i.id === 'loading');

    if (node.fileType === 'folder' && node.numberOfChild && hasLoadingChild) {
      handleAddChildern(node.id, node.directory_structure_ids);
    }
  };

  const nodeLevelMap = useMemo(() => {
    const levels = new Map<string, number>();
    const walk = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      level = 1
    ) => {
      nodes.forEach((node) => {
        levels.set(node.id, level);
        if (node.children) {
          walk(node.children, level + 1);
        }
      });
    };
    walk(treeData);
    return levels;
  }, [treeData]);

  const renderTree = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
    const tooltipLabel = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
      return showTooltipAsNodeName ? (
        <div>{node.label}</div>
      ) : (
        <div>
          {t('ecaDocList.saveSearch.createdBy')}
          <br />
          {node.createdBy || 'N/A'}
          <br />
          {node.createdDate
            ? getUTCDateTimeForDisplay(node.createdDate, ONLY_DATE_SHOW_FORMAT)
            : 'N/A'}
        </div>
      );
    };
    const newFolders = nodes.filter(
      (node) => node.fileType === 'folder' && node.newFolder
    );
    const folders = nodes.filter(
      (node) => node.fileType === 'folder' && !node.newFolder
    );
    const files = nodes.filter((node) => node.fileType !== 'folder');
    const newArray = [...newFolders, ...folders, ...files];

    const handleFolderEdit = (nodeId: string) => {
      setEditingNodeId(nodeId);
      setNewFolderName(findNodeById(treeData, nodeId)?.label);
      setCount(0);
      focusInputAfterDelay();
    };

    const focusInputAfterDelay = () => {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    };

    const nodeItemClicked = (
      e: any,
      node: TreeViewBaseItem<ExtendedTreeItemProps>,
      preSelectedItemLocation: boolean,
      isEdit: boolean
    ) => {
      if (!manageSavedSearch && node.newFolder && !isEdit) {
        handleFolderEdit(node.id);
      } else {
        selectedItemHandler(e, node, preSelectedItemLocation);
      }
    };

    return newArray.map((node) => {
      const nodeDepth = nodeLevelMap.get(node.id) ?? 1;
      const isHovered = hoveredNode === node.id;
      const isSelected = isArray(selectedItems)
        ? selectedItems.find((value) => value === node.id)
        : selectedItems === node.id;
      const preSelectedItemLocation = selectedNodeToMove === node.id;
      const isEditFolder = editingNodeId === node.id;
      const labelContent = (
        <div
          data-testid={`tree-node-${node.id}`}
          className="location-treeitem-label"
          onMouseEnter={() => setHoveredNode(node.id)}
          onMouseLeave={() => setHoveredNode(null)}
        >
                  <div
                    className={`location-treeitem ${node.newFolder ? 'new-input' : ''} ${
                      allowGlobalSelect &&
                      globalSelectedItem?.id &&
                      extractSelectedNodeId(String(node.id)) ===
                        extractSelectedNodeId(globalSelectedItem?.id ?? '')
                        ? 'globalSelectedTreeNode'
                        : undefined
                    }
                      ${node.newFolder && !isEditFolder && 'new-input-overwrite'}
                      ${node.newFolder && isEditFolder && 'new-input'}
                    ${
                      allowGlobalSelect &&
                      extractSelectedNodeId(globalSelectedItem?.id ?? '') !==
                        extractSelectedNodeId(String(node.id)) &&
                      extractSelectedNodeId(
                        globalSelectedScopeItem?.id ?? ''
                      ) === extractSelectedNodeId(String(node.id))
                        ? 'globalSelectedScope'
                        : ''
                    }        
                    `}
                  >
                    {getIconFromFileType(node.fileType)}
                    {allowGlobalSelect &&
                      extractSelectedNodeId(node.id) ===
                        extractSelectedNodeId(
                          globalSelectedScopeItem?.id ?? ''
                        ) && (
                        <AurIcon
                          icon={IconNames.ScopeIcon}
                          color={IconColors.Custom}
                          customClassName={'location-folder-icon scope-icon'}
                        />
                      )}
                    {isEditFolder ? (
                      <Typography className="typo-text body3_size custom-text-style">
                        <input
                          type="text"
                          aria-label={
                            node.fileType === 'folder'
                              ? t('eca.savedSearchSelectionDialog.folderName')
                              : t(
                                  'eca.savedSearchSelectionDialog.savedSearchName'
                                )
                          }
                          ref={inputRef}
                          name={node.id}
                          value={newFolderName || ''}
                          placeholder={
                            !manageSavedSearch
                              ? t('ecaDocList.saveSearch.untitledFolder')
                              : 'Untitled Group'
                          }
                          className="new-folder-input"
                          onChange={handleLabelChange}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          onBlur={(e) => {
                            if (
                              e.relatedTarget === inputRef.current ||
                              (e.relatedTarget &&
                                inputRef.current?.contains(e.relatedTarget))
                            ) {
                              return;
                            }
                            if (
                              count > 0 ||
                              node?.newFolder ||
                              (isRenameClicked && newFolderName == newTempName)
                            ) {
                              handleBlurOrEnter(node.id, !!node?.newFolder);
                            }
                          }}
                          onKeyDownCapture={(e) => {
                            e.stopPropagation();
                            if (e.key == 'Enter') {
                              handleBlurOrEnter(node.id, !!node?.newFolder);
                            }
                          }}
                          maxLength={50}
                        />
                      </Typography>
                    ) : (
                      <Typography
                        className="typo-text body3_size custom-text-style full-width-text"
                        sx={{flex: 1}}
                        component="span"
                      >
                        {node.label}
                        {node.count &&
                          node.fileType !== 'folder' &&
                          isShowCountLabel && (
                            <span
                              style={{color: '#A5B4CB'}}
                            >{` (${formatNumberWithComma(node.count)})`}</span>
                          )}
                      </Typography>
                    )}
                    {multipleSelectedLabel &&
                      node.fileType === 'folder' &&
                      (isSelected ||
                        node.children?.find(
                          (item) => item.id === selectedItems?.[0]
                        )) && (
                        <Typography
                          className="typo-text body4_size"
                          sx={{paddingRight: '8px'}}
                        >
                          {multipleSelectedLabel}
                        </Typography>
                      )}
                  </div>
                  {hasSavedSearchesPerm &&
                  showActions &&
                  isHovered &&
                  !node?.newFolder ? (
                    <div className="tree-action-container">
                      {node.fileType === 'folder' && nodeDepth !== 5 && (
                        <AurButton
                          key={0}
                          type="text"
                          title={t('eca.savedSearchSelectionDialog.addGroup')}
                          startIcon={
                            <AurIcon
                              icon={IconNames.Add}
                              color={IconColors.Custom}
                              customClassName="aur-responsive-tabset-dropdown-icon-color"
                            />
                          }
                          label={t('eca.savedSearchSelectionDialog.addGroup')}
                          size="Small"
                          onClick={() => handleAddFolder(node.id)}
                          classNames="aur-responsive-tabset-dropdown-icon-color"
                        />
                      )}

                      {!hideDropdownMenu &&
                      (!_.isUndefined(node.parentGroupId) ||
                        allowRootRename) ? (
                        <AurDropdownButton
                          data-testid="saved-search-overflow-icon"
                          elementID="overflowMenu"
                          classNames="aur-responsive-tabset-dropdown-icon"
                          type="text"
                          aria-live="polite"
                          size="Small"
                          iconType="Other"
                          menuItemHeight="medium"
                          menuItemWidth="medium"
                          menuItemTextSize="large"
                          menuItems={[
                            {
                              label: t('buttons.rename'),
                              onClick: handleRenameClick,
                            },
                            {
                              label: t('eca.flyout.savedSearch.move'),
                              onClick: () => {
                                handleSelect?.(
                                  t('eca.flyout.savedSearch.move'),
                                  node
                                );
                              },
                            },
                            {
                              label: t('buttons.delete'),
                              onClick: () => {
                                handleSelect?.(t('buttons.delete'), node);
                              },
                            },
                          ].filter((_i, index) =>
                            _.isUndefined(node.parentGroupId)
                              ? index == 0
                              : true
                          )}
                          menuDirectionLeft={false}
                          dropdownRole="DropDown"
                          hintText="Overflow Actions"
                          menuClassNames="saved-search-action-menu"
                        >
                          <AurIcon
                            icon={IconNames.More}
                            color={IconColors.Custom}
                            customClassName="aur-responsive-tabset-dropdown-icon-color"
                          />
                        </AurDropdownButton>
                      ) : null}
                    </div>
                  ) : null}
                  {hasSavedSearchesPerm && addFolderAllowed && (
                    <div className="location-treeitem-end">
                      {node.fileType == 'folder' &&
                        !node.newFolder &&
                        !node.hideAddFolder && (
                          <AurButton
                            classNames={
                              'drawer-close-button panel-close label-btn'
                            }
                            label={t('ecaDocList.saveSearch.newFolder')}
                            type="text"
                            size="Small"
                            title={t('ecaDocList.saveSearch.newFolder')}
                            onClick={() => handleAddFolder(node.id)}
                            onKeyDown={(e) => {
                              if (e.key == 'Enter' || e.key == ' ') {
                                handleAddFolder(node.id);
                              }
                            }}
                            startIcon={<AddIcon aria-hidden="true" />}
                          />
                        )}
                      {node.newFolder && (
                        <button
                          className="div-flex"
                          onClick={handleClick}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key == 'Enter' || e.key == ' ') {
                              handleClick(e);
                            }
                          }}
                        >
                          <MoreVertIcon />
                        </button>
                      )}
                    </div>
                  )}
                  {showCheckmark && isSelected && !preSelectedItemLocation && (
                    <AurIcon
                      icon={IconNames.CheckMark}
                      customClassName="menu-checkmark"
                      color={IconColors.Custom}
                    />
                  )}
                  {preSelectedItemLocation && (
                    <Typography className="typo-text body3_size custom-text-style full-width-text current-location">
                      {t(
                        'eca.savedSearchManagement.moveSavedSearchGroupPopUp.currentLocation'
                      )}
                    </Typography>
                  )}
        </div>
      );
      
      const treeLabel = (
        <AurTooltip
          open={showTooltip && isHovered && editingNodeId !== node.id}
          title={tooltipLabel(node)}
          placement="top"
          disableInteractive
          disableFocusListener
          className="typo-text label2_size tooltip-content"
          slotProps={{
            popper: {
              modifiers: [{name: 'offset', options: {offset: [0, -25]}}],
            },
          }}
        >
          {labelContent}
        </AurTooltip>
      );

      return (
        <span className="tooltip-padding" key={node.id}>
          <TreeItem
            sx={{
              paddingLeft: `calc(6px+var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth)})`,
            }}
            className={preSelectedItemLocation ? 'preselected-tree-item' : ''}
            key={node.id}
            itemId={node.id}
            onClick={(e) => {
              nodeItemClicked(e, node, preSelectedItemLocation, isEditFolder);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') {
                nodeItemClicked(e, node, preSelectedItemLocation, isEditFolder);
              }
            }}
            label={treeLabel}
          >
            {node.children && renderTree(node.children)}
          </TreeItem>
        </span>
      );
    });
  };

  const getNodeLevel = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    nodeId: string,
    level = 1
  ): number | null => {
    for (const node of nodes) {
      if (node.id === nodeId) return level;
      if (node.children) {
        const childLevel: number | null = getNodeLevel(
          node.children,
          nodeId,
          level + 1
        );
        if (childLevel) return childLevel;
      }
    }
    return null;
  };
  // Function to find a node by id
  const findNodeById = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    id: string
  ): TreeViewBaseItem<ExtendedTreeItemProps> | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };
  const findParentById = (
    treeData: TreeItem[],
    nodeId: string
  ): string | null => {
    for (const node of treeData) {
      if (node.children?.some((child) => child.id === nodeId)) {
        return node.id;
      }
      if (node.children) {
        const parent = findParentById(node.children, nodeId);
        if (parent) return parent;
      }
    }
    return null;
  };
  const initialDefaultSection = () => {
    if (defaultExpandedIds) {
      let fetchCallIndex = 0;
      let directory_structure_ids = defaultExpandedIds[0];

      const fetchChildrenData = () => {
        if (fetchCallIndex < defaultExpandedIds.length) {
          const id = defaultExpandedIds[fetchCallIndex];
          if (id.length > 0) {
            handleAddChildern(id, directory_structure_ids).then(() => {
              fetchCallIndex++;
              directory_structure_ids += `/${id}`;
              fetchChildrenData();
            });
          }
        }
      };

      fetchChildrenData();
    }
  };

  useEffect(() => {
    if (defaultExpandedIds) {
      initialDefaultSection();
    }
  }, []);

  const handleAddChildern = async (
    id: string,
    directory_structure_ids?: string
  ) => {
    if (getChildren) {
      const childerData = await getChildren?.(id, directory_structure_ids);
      childerData?.map((i: TreeViewBaseItem<ExtendedTreeItemProps>) => {
        addChildren(id, i);
      });
    }
  };

  const addChildren = (
    id: string,
    item: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    const addFolderToTree = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      parentId: string
    ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
      return nodes.map((node) => {
        if (node.id === parentId) {
          let existingChildren = node.children ?? [];
          if (existingChildren.length) {
            existingChildren = existingChildren.filter(
              (i) => !i.id.includes(`loading`)
            );
          }

          // Check if the item already exists in children
          const isDuplicate = existingChildren.some(
            (child) =>
              extractSavedSearchId(child.id) === extractSavedSearchId(item.id)
          );

          if (!isDuplicate) {
            return {
              ...node,
              children: [item, ...existingChildren], // Add only if not duplicate
            };
          }
        }
        if (node.children) {
          return {
            ...node,
            children: addFolderToTree(node.children, parentId),
          };
        }
        return node;
      });
    };

    setTreeData((prevData) => addFolderToTree(prevData, id));
  };

  const handleAddFolder = (id: string) => {
    if (manageSavedSearch) {
      dispatch(setAddGroupCheck(true));
    }

    const level = getNodeLevel(treeData, id);
    if (level && level >= 5) {
      return;
    }

    const addFolderToTree = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
      parentId: string
    ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
      return nodes.map((node) => {
        if (node.id === parentId) {
          const newId = `${node.id}-new-${(node.children || []).length + 1}`;
          setEditingNodeId(newId);
          const newFolder = {
            id: newId,
            label: '',
            fileType: 'folder',
            children: [],
            newFolder: true,
            parentGroupId: node.id,
          };
          setTimeout(() => {
            setTreeData((prevData) => updateNodeVisibility(prevData, parentId));
          }, 10000);
          return {
            ...node,
            hideAddFolder: true,
            children: [newFolder, ...(node.children || [])],
          };
        }
        if (node.children) {
          return {
            ...node,
            children: addFolderToTree(node.children, parentId),
          };
        }
        return node;
      });
    };

    setTreeData((prevData) => addFolderToTree(prevData, id));
    setCount(0);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    setExpandedIds((prev) => prev.concat([id]));
  };

  const handleAddRootFolder = () => {
    const newId = '1';
    setEditingNodeId(newId);
    const newFolder = {
      id: newId,
      label: '',
      fileType: 'folder',
      children: [],
      newFolder: true,
    };
    setTreeData([newFolder]);
    setCount(0);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };
  useEffect(() => {
    if (isCreatingRootFolder && isTemplate) {
      handleAddRootFolder();
    }
  }, [isCreatingRootFolder]);

  const updateNodeVisibility = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    parentId: string
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    return nodes.map((node) => {
      if (node.id === parentId) {
        return {
          ...node,
          hideAddFolder: false,
        };
      }

      if (node.children) {
        return {
          ...node,
          children: updateNodeVisibility(node.children, parentId),
        };
      }

      return node;
    });
  };

  const handleLabelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewFolderName(event.currentTarget.value);
    setCount((prev) => prev + 1);
  };

  const createNewGroup = async (parentId: string, name: string) => {
    if (!parentId && !isCreatingRootFolder) {
      return;
    }

    const reqBody = {
      name: name,
      parentGroupId: extractSavedSearchId(parentId),
    };

    try {
      let results = null;
      if (isTemplate && templateOperations) {
        results = await templateOperations.addNewGroup(
          templateOperations.templeteClientId,
          {...reqBody, parentGroupId: reqBody.parentGroupId || null}
        );
        templateOperations.setIsCreatingRootFolder(false);
        console.log('Group created:', results.data.groupId);
      } else {
        results = await addNewGroup(paramsClientProject, reqBody);
        console.log('Group created:', results.data.groupId);
      }
    } catch (error: unknown) {
      if (isTemplate && templateOperations)
        templateOperations.setIsCreatingRootFolder(false);
      console.log('Error creating group:', error);
    }
  };
  const params = useMemo(
    () => `?clientId=${clientId}&projectId=${projectId}`,
    [clientId, projectId]
  );
  const handleBlurOrEnter = async (nodeId: string, isNewGroup: boolean) => {
    const name = newFolderName?.trim() ?? '';

    const isEmptyNewGroup = isNewGroup && !newFolderName?.length;
    const invalidCharRegex = /[^0-9A-Za-z_ -]/g;
    const date = new Date();
    const day = date.toLocaleString('en-US', {day: '2-digit'});
    const month = date.toLocaleString('en-US', {month: '2-digit'});
    const year = date.toLocaleString('en-US', {year: '2-digit'});
    const formattedDate = `${month}-${day}-${year}`;
    if (invalidCharRegex.test(name)) {
      setTypedSpecialCharacter(true);
      setOpenErrorDialog(true);
      return;
    }
    if (manageSavedSearch) {
      if (!isEmptyNewGroup) {
        await createNewGroup(selectedIds, newFolderName as string);
        dispatch(setAddGroupCheck(false));
      }

      const deleteTreeItems = (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
      ): TreeViewBaseItem<ExtendedTreeItemProps>[] =>
        nodes
          .map((node) => ({
            ...node,
            children: deleteTreeItems(node.children ?? []),
          }))
          .filter((item) => item.id != editingNodeId);

      setTreeData((prevTreeData) => deleteTreeItems(prevTreeData));
      setEditingNodeId(null);

      const updatedTreeData = await fetchUpdatedRoot?.();
      if (!isEmpty(updatedTreeData)) {
        setTreeData(updatedTreeData ?? []);
      }

      if (isTemplate && templateOperations)
        templateOperations.setIsCreatingRootFolder(false);
    } else if (isRenameClicked) {
      const updateLabelInTree = (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
      ): TreeViewBaseItem<ExtendedTreeItemProps>[] =>
        nodes.map((node) => {
          if (node.id === nodeId) {
            const updatedLabel =
              newFolderName?.trim() ||
              node.label ||
              `New Search ${formattedDate}`;
            const updatedNode = {
              ...node,
              label: updatedLabel,
            };
            handleSelectedItem?.(updatedNode);
            return updatedNode;
          }
          return {
            ...node,
            children: node.children ? updateLabelInTree(node.children) : [],
          };
        });

      setTreeData((prevTreeData) => updateLabelInTree(prevTreeData));
      const parentId = findParentById(treeData, nodeId);
      const selectedNode = findNodeById(treeData, selectedIds)?.fileType;
      if (newFolderName !== newTempName && manageSearchRename) {
        const isFolderCondition = () =>
          isTemplate
            ? handleUpdateSavedSearchGroupTemplate(parentId)
            : handleUpdateSavedSearch(parentId as string);
        const isNodeCondition = () =>
          isTemplate ? handleUpdateSavedSearchTemplate() : handleUpdateName();
        selectedNode === 'folder'
          ? isFolderCondition() // call API to update saved search
          : isNodeCondition();
      }
      setEditingNodeId(null);
      setCount(1);
    } else {
      const updateLabelInTree = (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
      ): TreeViewBaseItem<ExtendedTreeItemProps>[] =>
        nodes.map((node) => {
          if (node.id === nodeId) {
            const newNode = {
              ...node,
              label: newFolderName?.trim() || `New Search ${formattedDate}`,
            };
            handleSelectedItem?.(newNode);

            return newNode;
          }
          if (node.children) {
            return {
              ...node,
              children: updateLabelInTree(node.children),
            };
          }
          return node;
        });

      setTreeData((prevTreeData) => updateLabelInTree(prevTreeData));
    }
    dispatch(setManageSavedSearch(true));
    setEditingNodeId(null);
    setNewFolderName('');
  };

  const handleDeleteClick = () => {
    if (selectedItems) {
      const deleteTreeItems = (
        nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
      ): TreeViewBaseItem<ExtendedTreeItemProps>[] =>
        nodes
          .map((node) => ({
            ...node,
            children: deleteTreeItems(node.children ?? []),
          }))
          .filter((item) => item.id != selectedItems);

      setTreeData((prevTreeData) => deleteTreeItems(prevTreeData));
    }
  };
  return (
    <>
      {treeData && treeData.length > 0 ? (
        <>
          <SimpleTreeView
            aria-label={t('eca.savedSearchSelectionDialog.savedSearchTree')}
            selectedItems={selectedItems}
            multiSelect={multipleSelection}
            onSelectedItemsChange={handleSelectedItemsChange}
            itemChildrenIndentation={24}
            expansionTrigger="iconContainer"
            experimentalFeatures={{
              indentationAtItemLevel: true,
            }}
            onExpandedItemsChange={(_event, ids) => {
              multipleSelection && _event.stopPropagation();
              setExpandedIds(ids);
            }}
            expandedItems={expandedIds}
            onItemFocus={(_, itemId: string) => {
              setHoveredNode(itemId);
            }}
          >
            {renderTree(treeData)}
          </SimpleTreeView>
          <AurMenu
            anchorElement={anchorEl}
            shouldShow={open}
            onClose={handleClose}
            menuItems={[
              {
                label: t('ecaDocList.saveSearch.rename'),
                onClick: handleRenameClick,
              },
              {
                label: t('ecaDocList.saveSearch.delete'),
                onClick: handleDeleteClick,
              },
            ]}
            direction="left"
            aria-labelledby="basic-button"
          />
        </>
      ) : (
        <Typography className="typo-text body3_size custom-text">
          {handleNoResultMessage ?? t('ecaDocList.saveSearch.noSavedSearch')}
        </Typography>
      )}
      {typedSpecialCharacter &&
      (manageSavedSearch || isRenameClicked || isFromEcaSaveSearch) ? (
        <ErrorDialog
          title={t(`ecaDocList.saveSearch.popupTitle`)}
          description={t(`ecaDocList.saveSearch.popupContent`)}
          button={t(`ecaDocList.saveSearch.popupOkLabel`)}
          open={openErrorDialog}
          onOkClicked={closeHandler}
        />
      ) : null}
    </>
  );
};

export default AurLocationTreeView;
